import React, { useMemo, useState, useRef } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Rectangle, Dot, Legend, ReferenceArea, ReferenceLine, ResponsiveContainer, ComposedChart, Customized, Scatter, Cell, Bar } from 'recharts';
import PerinTooltip from './components/PerinTooltip';
import { useSelector } from 'react-redux';
import { getGraphXValue } from '../../../utils/dateUtils';
import { getAlertColors } from '../../../utils/alertUtils';
import { sortData } from '../../../utils/graphUtils';
import { Modal, Button, Form } from 'react-bootstrap';
import { formatValueByModalityId } from '../../../utils/unitUtils';

const PerinComposedChart = ({
    data,
    vitalName,
    vitals,
    deviceNamesData,
    opacity,
    handleLegendClick,
    handlePointClick,
    highestMeasurement,
    lowestMeasurement,
    alertsForPeriod
}) => {
    const { averageGrouped, readingInterval } = useSelector((state) => state.patientDetailFilter);
    const userUnitPreference = useSelector((state) => state.userInfo.unitType);
    const dateFormat = useSelector((state) => state.userInfo.dateFormat);
    const {deviceSystemRules} = useSelector((state) => state.dropdowns);
    
    // Annotation state
    const [annotations, setAnnotations] = useState([]);
    const [isAnnotationModalOpen, setIsAnnotationModalOpen] = useState(false);
    const [currentAnnotation, setCurrentAnnotation] = useState({ date: '', text: '', yValue: null });
    const [editingAnnotationId, setEditingAnnotationId] = useState(null);
    const chartRef = useRef(null);


    const colors = {
        0: '#7A6B78',
        1: '#00162E',
    };

    const convertValue = (value, isThreshold = false) => {
        if (value === 0 || value === undefined || value === null) return value;

        switch (vitalName) {
            case 'temperature':
                // Always convert thresholds from Celsius, measurements are pre-converted
                if (userUnitPreference === 'Imperial') {
                    const val = isThreshold ? (value * 9 / 5) + 32 : value;
                    return parseFloat(val.toFixed(1));
                }
                return parseFloat(value.toFixed(1));
            case 'weight':
                // Always convert thresholds from Kilograms, measurements are pre-converted
                if (userUnitPreference === 'Imperial') {
                    const val = isThreshold ? value * 2.20462 : value;
                    return parseFloat(val.toFixed(1));
                }
                return parseFloat(value.toFixed(1));
            default:
                return parseFloat(value.toFixed(1));
        }
    };

    // Convert threshold values from base units (Celsius/Kilograms)
    const convertedUpperLimit = convertValue(vitals?.upperLimit, true);
    const convertedLowerLimit = convertValue(vitals?.lowerLimit, true);
    const convertedTargetValue = convertValue(vitals?.targetValue, true);

    // Measurements are already in the correct unit
    const convertedHighest = convertValue(highestMeasurement, false);
    const convertedLowest = convertValue(lowestMeasurement, false);


    const calculateDomain = () => {
        let minDomain = convertedLowerLimit;
        let maxDomain = convertedUpperLimit;

        const hasLowerLimit = convertedLowerLimit !== 0 && convertedLowerLimit !== null && convertedLowerLimit !== undefined;
        const hasUpperLimit = convertedUpperLimit !== 0 && convertedUpperLimit !== null && convertedUpperLimit !== undefined;

        if (convertedLowest !== null && convertedLowest !== undefined) {
            if (!hasLowerLimit || convertedLowest < minDomain) {
                minDomain = convertedLowest;
            }
        }

        if (convertedHighest !== null && convertedHighest !== undefined && convertedHighest !== 0) {
            if (!hasUpperLimit || convertedHighest > maxDomain) {
                maxDomain = convertedHighest;
            }
        }

        const isSingleValue = convertedLowest === convertedHighest && convertedLowest !== null && convertedLowest !== undefined;
        
        let range = maxDomain - minDomain;
        
        // If range is 0 or very small (single value case), create a reasonable range
        if (range === 0 || (isSingleValue && range < 0.01)) {
            const singleValue = convertedLowest !== null ? convertedLowest : (convertedHighest !== null ? convertedHighest : 0);
            
            // Use a percentage-based padding (10% of the value, or minimum 1 unit)
            const padding = Math.max(Math.abs(singleValue) * 0.1, 1);
            
            minDomain = singleValue - padding;
            maxDomain = singleValue + padding;
            range = maxDomain - minDomain;
        }

        const bufferPercentage = 0.2;
        const buffer = range * bufferPercentage;
        const newMin = minDomain - buffer < 0 && minDomain >= 0 ? minDomain : minDomain - buffer;

        const formattedMin = formatValueByModalityId(newMin, vitals?.modalityId);
        const formattedMax = formatValueByModalityId(maxDomain + buffer, vitals?.modalityId);
        return [formattedMin, formattedMax];
    };

    const domain = calculateDomain();

    const generateTicks = () => {
        let ticks = domain;
        if (convertedLowerLimit !== 0) {
            ticks.push(convertedLowerLimit);
        }
        if (convertedTargetValue !== 0) {
            ticks.push(convertedTargetValue);
        }
        if (convertedUpperLimit !== 0) {
            ticks.push(convertedUpperLimit);
        }

        const uniqueTicks = Array.from(new Set(ticks.filter(tick => tick !== undefined && tick !== null))).sort((a, b) => a - b);

        return uniqueTicks.map((item) => formatValueByModalityId(item, vitals?.modalityId));
    };

    const ScatterDot = ({ cx, cy }) => {
        return (
            <Dot cx={cx} cy={cy} fill="#828282" r={2} />
        )
    };

    const CustomizedRectangle = ({ formattedGraphicalItems }) => {
        const firstSeries = formattedGraphicalItems[0];
        const secondSeries = formattedGraphicalItems[1];

        return firstSeries?.props?.points?.map((firstSeriesPoint, index) => {
            const secondSeriesPoint = secondSeries?.props?.points[index];
            
            // Safety check: ensure both points exist and have required properties
            if (!firstSeriesPoint || !secondSeriesPoint || !firstSeriesPoint.payload) {
                return null;
            }
            
            const yDifference = firstSeriesPoint.y - secondSeriesPoint.y;
            let fillColor = 'lightgrey';
            const key = `${firstSeriesPoint.payload.name || 'point'}-${index}-${firstSeriesPoint.y}-${secondSeriesPoint.y}`;

            return (
                <Rectangle
                    key={key}
                    width={1}
                    height={yDifference}
                    x={secondSeriesPoint.x + 4}
                    y={secondSeriesPoint.y + 5}
                    fill={fillColor}
                    stroke={fillColor}
                    strokeWidth={1}
                    strokeOpacity={0.3}
                    style={{ pointerEvents: 'none' }}
                />
            );
        });
    };

    const sortedDataWithAlerts = useMemo(() => {
        const alertsForModality = alertsForPeriod.filter(
            (alert) => {
                if (vitals?.modalityType?.toLowerCase().includes('ecg')) {
                    return alert.alert_name.toLowerCase().includes('fast recovery') || alert.alert_modalityId === vitals.modalityId;
                } else {
                    return alert.modalityId === vitals.modalityId
                }
            }
        );

        const alertData = alertsForModality.map(alert => ({
            date: getGraphXValue(alert.createdDate, dateFormat, averageGrouped, readingInterval),
            alertY: domain[1],
            alertYBottom: domain[0],
            alert: alert
        }));

        let updatedData = [...data];

        alertData.forEach(alertItem => {
            const matchIndex = updatedData.findIndex(item => item.date === alertItem.date);
            if (matchIndex !== -1) {
                updatedData[matchIndex] = { ...updatedData[matchIndex], alertY: alertItem.alertY, alertYBottom: alertItem.alertYBottom, alert: alertItem.alert };
            } else {
                updatedData.push(alertItem);
            }
        });

        updatedData.sort(sortData);

        return updatedData;
    }, [data, alertsForPeriod, vitals?.modalityId]);

    const BarWithIcon = (props) => {
        const { fill, x, y, width, height, payload } = props;
        
        // Safety check: ensure payload exists
        if (!payload) {
            return null;
        }

        const value = payload.alertY;
        if (value == null || value === 0) {
            return null;
        }

        const barCenterX = x + width / 2;
    
        const domainRange = domain[1] - domain[0];
        
        let barTopY = y; 
        let barBottomY;
        let actualHeight;

        if (domain[0] < 0 && domain[1] > 0) {
            const heightRatio = domainRange / domain[1];
            actualHeight = height * heightRatio;
        } else {
            actualHeight = height;
        }
        
        barBottomY = barTopY + actualHeight;

        const lineWidth = width;

        const iconWidth = 16.6;
        const iconHeight = 14.6;

        const rectTopY = Math.min(barTopY, barBottomY);
        const rectBottomY = Math.max(barTopY, barBottomY);
        const rectHeight = Math.abs(rectBottomY - rectTopY);

        return (
            <g>
                <rect
                    x={barCenterX - lineWidth / 2}
                    y={rectTopY}
                    width={lineWidth}
                    height={rectHeight}
                    fill={fill}
                />
                <svg
                    x={barCenterX - iconWidth / 2}
                    y={rectBottomY - 9}
                    width={iconWidth}
                    height={iconHeight}
                    viewBox="0 0 25 22"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.1536 1.16515C12.3076 0.898483 12.6925 0.898482 12.8465 1.16515L23.9718 20.4348C24.1258 20.7015 23.9333 21.0348 23.6254 21.0348H1.37472C1.0668 21.0348 0.874348 20.7015 1.02831 20.4348L12.1536 1.16515Z"
                        fill="white"
                    />
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.1536 1.16515C12.3076 0.898483 12.6925 0.898482 12.8465 1.16515L23.9718 20.4348C24.1258 20.7015 23.9333 21.0348 23.6254 21.0348H1.37472C1.0668 21.0348 0.874348 20.7015 1.02831 20.4348L12.1536 1.16515ZM11.3409 8.56895C11.3409 7.94615 11.8458 7.44127 12.4686 7.44127C13.0914 7.44127 13.5963 7.94615 13.5963 8.56895V14.2074C13.5963 14.8302 13.0914 15.3351 12.4686 15.3351C11.8458 15.3351 11.3409 14.8302 11.3409 14.2074V8.56895ZM12.4687 17.4009C11.8459 17.4009 11.341 17.9058 11.341 18.5286C11.341 19.1514 11.8459 19.6563 12.4687 19.6563C13.0915 19.6563 13.5964 19.1514 13.5964 18.5286C13.5964 17.9058 13.0915 17.4009 12.4687 17.4009Z"
                        fill={fill}
                    />
                </svg>
            </g>
        );
    };

    // Handle chart click to add annotation
    const handleChartClick = (e) => {
        // Don't trigger if clicking on interactive elements
        const target = e.target;
        const tagName = target.tagName?.toLowerCase();
        
        // Skip if clicking on circles (data points), text, or SVG elements that are interactive
        if (
            tagName === 'circle' || 
            tagName === 'text' ||
            tagName === 'path' ||
            target.closest('circle') || 
            target.closest('text') ||
            target.closest('.recharts-legend-item') ||
            target.closest('.recharts-tooltip-wrapper') ||
            target.closest('.recharts-active-dot') ||
            target.closest('svg[viewBox="0 0 25 24"]') // Skip SQI warning icons
        ) {
            return;
        }

        // Get the chart container bounds
        const chartContainer = e.currentTarget;
        const rect = chartContainer.getBoundingClientRect();
        
        // Calculate click position relative to chart
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        
        // Find the closest data point based on X position
        const chartWidth = rect.width;
        const dataLength = sortedDataWithAlerts.length;
        
        if (dataLength === 0) return;
        
        // Estimate which data point was clicked based on X position
        // Account for margins (approximately 5% on each side)
        const marginLeft = chartWidth * 0.05;
        const marginRight = chartWidth * 0.05;
        const plotWidth = chartWidth - marginLeft - marginRight;
        const relativeX = clickX - marginLeft;
        
        if (relativeX < 0 || relativeX > plotWidth) return;
        
        const xRatio = relativeX / plotWidth;
        const dataIndex = Math.floor(xRatio * dataLength);
        const clickedDataPoint = sortedDataWithAlerts[Math.min(Math.max(dataIndex, 0), dataLength - 1)];
        
        if (!clickedDataPoint) return;
        
        // Calculate Y value from click position (approximate)
        // Account for margins (approximately 10% top, 5% bottom)
        const marginTop = rect.height * 0.1;
        const marginBottom = rect.height * 0.05;
        const plotHeight = rect.height - marginTop - marginBottom;
        const relativeY = clickY - marginTop;
        
        if (relativeY < 0 || relativeY > plotHeight) return;
        
        const yRatio = 1 - (relativeY / plotHeight); // Invert because Y=0 is at top
        const yValue = domain[0] + (yRatio * (domain[1] - domain[0]));
        
        // Open modal to add annotation
        setCurrentAnnotation({
            date: clickedDataPoint.date,
            text: '',
            yValue: parseFloat(yValue.toFixed(1))
        });
        setEditingAnnotationId(null);
        setIsAnnotationModalOpen(true);
    };

    // Handle saving annotation
    const handleSaveAnnotation = () => {
        if (!currentAnnotation.text.trim()) {
            return;
        }

        if (editingAnnotationId !== null) {
            // Update existing annotation
            setAnnotations(annotations.map(ann => 
                ann.id === editingAnnotationId 
                    ? { ...ann, text: currentAnnotation.text, date: currentAnnotation.date, yValue: currentAnnotation.yValue }
                    : ann
            ));
        } else {
            // Add new annotation
            const newAnnotation = {
                id: Date.now(),
                date: currentAnnotation.date,
                text: currentAnnotation.text,
                yValue: currentAnnotation.yValue
            };
            setAnnotations([...annotations, newAnnotation]);
        }

        setIsAnnotationModalOpen(false);
        setCurrentAnnotation({ date: '', text: '', yValue: null });
        setEditingAnnotationId(null);
    };

    const handleDeleteAnnotation = (id) => {
        setAnnotations(annotations.filter(ann => ann.id !== id));
    };

    const handleEditAnnotation = (annotation) => {
        setCurrentAnnotation({
            date: annotation.date,
            text: annotation.text,
            yValue: annotation.yValue
        });
        setEditingAnnotationId(annotation.id);
        setIsAnnotationModalOpen(true);
    };

    const AnnotationRenderer = (props) => {
        if (!annotations.length) return null;

        const { width, height, offset } = props;
        if (!width || !height) return null;

        const chartWidth = width - (offset?.left || 0) - (offset?.right || 0);
        const chartHeight = height - (offset?.top || 0) - (offset?.bottom || 0);
        const startX = offset?.left || 0;
        const startY = offset?.top || 0;

        return (
            <g>
                {annotations.map((annotation) => {
                    // Find the data point index for this annotation date
                    const dataIndex = sortedDataWithAlerts.findIndex(d => d.date === annotation.date);
                    if (dataIndex === -1) return null;

                    // Calculate X position based on data index
                    const dataLength = sortedDataWithAlerts.length;
                    const xRatio = dataLength > 1 ? dataIndex / (dataLength - 1) : 0;
                    const xPosition = startX + (xRatio * chartWidth);
                    
                    // Calculate Y position from yValue
                    const yRatio = (annotation.yValue - domain[0]) / (domain[1] - domain[0]);
                    const yPosition = startY + chartHeight - (yRatio * chartHeight);

                    return (
                        <g key={annotation.id}>
                            {/* Vertical line */}
                            <line
                                x1={xPosition}
                                y1={startY}
                                x2={xPosition}
                                y2={startY + chartHeight}
                                stroke="#4A90E2"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                opacity={0.6}
                                style={{ pointerEvents: 'none' }}
                            />
                            {/* Annotation marker */}
                            <circle
                                cx={xPosition}
                                cy={yPosition}
                                r={5}
                                fill="#4A90E2"
                                stroke="white"
                                strokeWidth={2}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditAnnotation(annotation);
                                }}
                            />
                            {/* Annotation text background */}
                            <rect
                                x={xPosition + 6}
                                y={yPosition - 20}
                                width={Math.min(annotation.text.length * 6 + 8, 150)}
                                height={16}
                                fill="white"
                                fillOpacity={0.9}
                                stroke="#4A90E2"
                                strokeWidth={1}
                                rx={2}
                            />
                            {/* Annotation text */}
                            <text
                                x={xPosition + 10}
                                y={yPosition - 8}
                                fill="#4A90E2"
                                fontSize={11}
                                fontWeight="bold"
                                style={{ cursor: 'pointer', pointerEvents: 'none' }}
                            >
                                {annotation.text.length > 25 ? annotation.text.substring(0, 25) + '...' : annotation.text}
                            </text>
                        </g>
                    );
                })}
            </g>
        );
    };


    return (
        <>
            <div 
                // onClick={handleChartClick}
                style={{ cursor: 'crosshair', width: '100%', height: '250px' }}
            >
                <ResponsiveContainer width={"100%"} height={250}>
                    <ComposedChart
                        ref={chartRef}
                        data={sortedDataWithAlerts}
                        margin={{ bottom: 5 }}
                    >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    fontSize={12}
                    padding={{ right: 5 }}
                />
                <YAxis
                    domain={domain}
                    ticks={generateTicks()}
                    fontSize={12}
                />
                <Tooltip content={<PerinTooltip vitals={vitals} systemRules={deviceSystemRules}/>} />
                <Legend
                    verticalAlign="top"
                    height={30}
                    align={"right"}
                    iconType="square"
                    wrapperStyle={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}
                    payload={deviceNamesData.map((deviceName, index) => ({
                        value: deviceName,
                        type: 'square',
                        color: colors[index] || '#000000',
                    }))}
                    onClick={handleLegendClick}
                />

                {convertedUpperLimit !== 0 &&
                    <>
                        <ReferenceLine y={convertedUpperLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                        <ReferenceArea y1={domain[1]} y2={convertedUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
                    </>
                }
                {convertedLowerLimit !== 0 &&
                    <>
                        <ReferenceLine y={convertedLowerLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                        <ReferenceArea y1={convertedLowerLimit} y2={domain[0]} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
                    </>
                }
                {convertedTargetValue !== 0 &&
                    <ReferenceLine y={convertedTargetValue} stroke="green" strokeDasharray="3 3" />
                }
                {/* Render annotation markers, lines, and text */}
                <Customized component={AnnotationRenderer} />
                <Bar dataKey="alertY" barSize={1} baseValue={domain[0]} shape={<BarWithIcon />}>
                    {sortedDataWithAlerts.map((entry, index) => {
                        const alert = entry.alert;
                        const alertColors = getAlertColors(alert?.alert_color ?? '')
                        let fillColor = alertColors.color;
                        return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                </Bar>

                {deviceNamesData?.map((deviceName, index) => (
                    <React.Fragment key={`${deviceName}-${index}`}>
                        {averageGrouped && opacity[deviceName] !== 0 &&
                            <>
                                <Customized component={CustomizedRectangle} />
                                <Scatter
                                    dataKey={`${deviceName}.${vitalName}-range.min`}
                                    activeDot={false}
                                    shape={<ScatterDot />}
                                />
                                <Scatter
                                    dataKey={`${deviceName}.${vitalName}-range.max`}
                                    activeDot={false}
                                    shape={<ScatterDot />}
                                />
                            </>

                        }
                        {/* dashed line to connect null values, no dots */}
                        <Line
                            connectNulls={true}
                            key={`${deviceName}-nulls`}
                            type="linear"
                            dataKey={`${deviceName}.${vitalName}`}
                            stroke={"#D6D6D6"}
                            strokeOpacity={opacity[deviceName] || 0}
                            strokeWidth={2}
                            dot={(props) => {
                                return null;
                            }}
                            activeDot={(props) => {
                                return null;
                            }}
                        />

                        {/* line with actual data points */}
                        <Line
                            connectNulls={false}
                            key={deviceName}
                            type="linear"
                            dataKey={`${deviceName}.${vitalName}`}
                            stroke={colors[index] || '#000000'}
                            strokeOpacity={opacity[deviceName] || 0}
                            strokeWidth={2}
                            dot={(props) => {
                                const { cx, cy, value, key, payload } = props;
                                
                                // Safety check: ensure payload[deviceName] exists
                                if (!payload || !payload[deviceName]) {
                                    return null;
                                }
                                
                                let dotColor = colors[index];
                                let fillColor = 'white';
                                if (payload[deviceName]?.[`${vitalName}_adhoc`] === true && !averageGrouped) {
                                    dotColor = '#B8860B';
                                    fillColor = '#B8860B';
                                } else if ((convertedLowerLimit !== 0 && value < convertedLowerLimit) || (value > convertedUpperLimit && convertedUpperLimit !== 0)) {
                                    dotColor = '#CA3B3B';
                                    fillColor = '#CA3B3B';
                                } else if (value === convertedTargetValue && convertedTargetValue !== 0) {
                                    dotColor = '#2A7F38';
                                    fillColor = '#2A7F38';
                                }
                                if (value !== undefined && value !== null && !isNaN(value)) {
                                    if (payload[deviceName][`${vitalName}_sqi`] === "acceptable" && !averageGrouped) {
                                        const dotSize = 12;
                                        const dotHalf = dotSize / 2;
                                        return (
                                            <svg
                                                x={cx - dotHalf}
                                                y={cy - dotHalf}
                                                width={dotSize}
                                                height={dotSize}
                                                viewBox="0 0 25 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <rect x="0.5" width="24" height="24" rx="12" fill="#212121" />
                                                <path
                                                    d="M12.4956 19C12.0819 19 11.7292 18.8527 11.4375 18.5581C11.1458 18.2635 11 17.9094 11 17.4956C11 17.0819 11.1473 16.7292 11.4419 16.4375C11.7365 16.1458 12.0906 16 12.5044 16C12.9181 16 13.2708 16.1473 13.5625 16.4419C13.8542 16.7365 14 17.0906 14 17.5044C14 17.9181 13.8527 18.2708 13.5581 18.5625C13.2635 18.8542 12.9094 19 12.4956 19ZM11 14V5H14V14H11Z"
                                                    fill="#F5C10D"
                                                />
                                            </svg>
                                        );
                                    }
                                    return (
                                        <circle
                                            cx={cx}
                                            cy={cy}
                                            r={3}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePointClick(deviceName, payload[deviceName]);
                                            }}
                                            style={{ pointerEvents: 'visible' }}
                                            fill={opacity[deviceName] === 0 ? 'transparent' : fillColor}
                                            stroke={dotColor}
                                            strokeOpacity={opacity[deviceName] || 0}
                                            strokeWidth={2}
                                            key={key}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            }}
                            activeDot={(props) => {
                                const { cx, cy, value, key, payload } = props;
                                
                                // Safety check: ensure payload[deviceName] exists
                                if (!payload || !payload[deviceName]) {
                                    return null;
                                }
                                
                                let dotColor = colors[index];
                                if ((convertedLowerLimit !== 0 && value < convertedLowerLimit) || (value > convertedUpperLimit && convertedUpperLimit !== 0)) {
                                    dotColor = '#CA3B3B';
                                } else if (value === convertedTargetValue && convertedTargetValue !== 0) {
                                    dotColor = '#2A7F38';
                                }
                                // if (value !== undefined && value !== null && !isNaN(value) && payload[deviceName][`${vitalName}_sqi`] !== "unacceptable") {
                                if (value !== undefined && value !== null && !isNaN(value)) {
                                    if (payload[deviceName][`${vitalName}_sqi`] === "acceptable" && !averageGrouped) {
                                        const dotSize = 18;
                                        const dotHalf = dotSize / 2;
                                        return (
                                            <svg
                                                x={cx - dotHalf}
                                                y={cy - dotHalf}
                                                width={dotSize}
                                                height={dotSize}
                                                viewBox="0 0 25 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePointClick(deviceName, payload[deviceName]);
                                                }}
                                                style={{ pointerEvents: 'visible' }}
                                            >
                                                <rect x="0.5" width="24" height="24" rx="12" fill="#212121" />
                                                <path
                                                    d="M12.4956 19C12.0819 19 11.7292 18.8527 11.4375 18.5581C11.1458 18.2635 11 17.9094 11 17.4956C11 17.0819 11.1473 16.7292 11.4419 16.4375C11.7365 16.1458 12.0906 16 12.5044 16C12.9181 16 13.2708 16.1473 13.5625 16.4419C13.8542 16.7365 14 17.0906 14 17.5044C14 17.9181 13.8527 18.2708 13.5581 18.5625C13.2635 18.8542 12.9094 19 12.4956 19ZM11 14V5H14V14H11Z"
                                                    fill="#F5C10D"
                                                />
                                            </svg>
                                        );
                                    }
                                    return (
                                        <circle
                                            cx={cx}
                                            cy={cy}
                                            r={4}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePointClick(deviceName, payload[deviceName]);
                                            }}
                                            fill={opacity[deviceName] === 0 ? 'transparent' : dotColor}
                                            stroke={opacity[deviceName] === 0 ? 'transparent' : dotColor}
                                            style={{ pointerEvents: 'visible' }}
                                            strokeOpacity={opacity[deviceName] || 0}
                                            strokeWidth={2}
                                            key={key}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        />
                    </React.Fragment>
                ))}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

        {/* Annotation Modal */}
        <Modal show={isAnnotationModalOpen} onHide={() => {
            setIsAnnotationModalOpen(false);
            setCurrentAnnotation({ date: '', text: '', yValue: null });
            setEditingAnnotationId(null);
        }} centered>
            <Modal.Header closeButton>
                <Modal.Title>{editingAnnotationId !== null ? 'Edit Annotation' : 'Add Annotation'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                            type="text"
                            value={currentAnnotation.date}
                            disabled
                            readOnly
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Y Value</Form.Label>
                        <Form.Control
                            type="text"
                            value={currentAnnotation.yValue !== null ? currentAnnotation.yValue.toFixed(1) : ''}
                            disabled
                            readOnly
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Annotation Text *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={currentAnnotation.text}
                            onChange={(e) => setCurrentAnnotation({ ...currentAnnotation, text: e.target.value })}
                            placeholder="Enter your annotation..."
                            autoFocus
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {editingAnnotationId !== null && (
                    <Button
                        variant="danger"
                        onClick={() => {
                            handleDeleteAnnotation(editingAnnotationId);
                            setIsAnnotationModalOpen(false);
                            setCurrentAnnotation({ date: '', text: '', yValue: null });
                            setEditingAnnotationId(null);
                        }}
                    >
                        Delete
                    </Button>
                )}
                <Button variant="secondary" onClick={() => {
                    setIsAnnotationModalOpen(false);
                    setCurrentAnnotation({ date: '', text: '', yValue: null });
                    setEditingAnnotationId(null);
                }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveAnnotation} disabled={!currentAnnotation.text.trim()}>
                    {editingAnnotationId !== null ? 'Update' : 'Add'}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default PerinComposedChart;
